// Where the observer is among the bodies, frame by frame (observer-centric LOD, step 7). No three.js here: the renderer hands in the camera's position and heading after its controls have
// moved it and takes back what to write to the camera.
//
// Two regimes, one of them chosen by where the observer is.
//   tile  The observer is near the home planet. The controls work on the flat tile as they always did (camera x, z on the tile, y the height above the sea); the registry's chart lays the
//         tile on the sphere (bodies.ts), so the observer's place among the bodies is a function of the camera's position and nothing here has to move it.
//   free  The observer is high above the home planet (above FREE_ABOVE_M) or near any other body. The observer's place is the master: a position relative to the body the picture follows
//         (the nearest, with hysteresis), in universe axes. The controls still add their step to the camera each frame, as in the tile; what they added, in render axes, is taken as the
//         displacement, turned into universe axes through the axes of the pose it was made in, and the camera is then set from the new place (over the home planet: the tile coordinates
//         of the point below it, so everything that reads the camera's x and z still gets a sensible number; over another body: held over the tile's own centre, the height is the
//         altitude). Moving on the tile is moving on the sphere at the surface; moving here is moving in space, at any height. The speed is the renderer's: it grows with the height
//         above the nearest surface (DefaultSimRenderer, FLY_ANGULAR).
// DYNAMICS. With the thrusters on (`drive`), the free regime is not kinematic: the observer is a body of its own with a velocity (orbit.ts), the bodies pull on it as the server's circles say
// (gravity.ts) and the keys are thrusters. The controls then move nothing themselves; the caller says which way to push (render axes of the pose of the last frame) and the observer is advanced
// by the ticks the simulation has run (simclock.ts). Entering starts the observer moving with the followed body. Without the server's bodies (an old server) or a tick, the free regime is as
// above, kinematic.
// A change of regime or of followed body changes the render axes, never the attitude of the camera: its direction AND its way up are re-expressed (bodies.ts rebaseAttitude), so nothing
// moves on the screen when the frame under it is replaced. In the free regime the same holds for every step: moving turns the axes (up is the radial direction), and the attitude is
// re-expressed each frame, so the camera is inertial: no body pulls it down, a change of followed body does not turn it over, and no up is preferred (the renderer turns it about its own
// axes). On the tile the heading is held against the tile, and turns with the axes as it does for anyone walking round a sphere.
import { FREE_ABOVE_M, TILE_BELOW_M, len, norm, scale, sub, add, rebaseAttitude, renderToUniverse } from './bodies';
import type { Bodies, Pose, V3 } from './bodies';
import { strongest } from './gravity';
import { Orbiter } from './orbit';

/** Closest an observer may come to the surface of a body that has no tile (m): no landing on the moon. */
export const MIN_ROCK_ALT_M = 20e3;

/** Time constant (s) of the camera's roll easing back to level once the observer is on the home tile again (the tile has a ground and a horizon). Out in space nothing levels the camera. */
export const ROLL_LEVEL_TAU_S = 1.5;

/** Full thrust is this many times the pull of the body that pulls hardest where the observer is (so a push is a few g near a planet and a few thousandths of one far out). The wheel scales it. */
export const THRUST_G = 4;

/** What the thrusters are asked for this frame (the observer among the bodies, with dynamics on). */
export interface Drive {
  /** The simulation's tick now (simclock.ts); NaN until known (the free regime is kinematic then). */
  tick: number;
  /** The direction to push, render axes of the pose of the last frame (x east, y away from the followed body, z south); unit length or zero. */
  dir: ArrayLike<number>;
  /** The thrust scale: the wheel and the sprint key. */
  gain: number;
  /** Take off the velocity relative to the followed body (the brake), whatever `dir` says. */
  brake: boolean;
}

export interface FlightOut {
  /** The camera's position (three's axes, tile coordinates) and heading (camera yaw and pitch, 'YXZ'). */
  x: number; y: number; z: number;
  yaw: number; pitch: number;
  /** The camera's roll about its view axis (camera.rotation 'YXZ' third angle): the attitude's own out in space, easing to zero on the tile. Always set; apply it every frame. */
  roll: number;
  /** True when the caller must write the camera from the above (it moved the camera itself otherwise). */
  changed: boolean;
}

export class Flight {
  mode: 'tile' | 'free' = 'tile';
  /** The followed body (index in the registry). */
  body = 0;
  /** The pose of this frame: null until the registry has been fed. */
  pose: Pose | null = null;
  private rel: V3 = [0, 0, 0];
  private last: V3 = [0, 0, 0];
  /** The observer as a body of its own (the star's frame): set while the thrusters are on in the free regime, else null. */
  orbiter: Orbiter | null = null;
  /** The thrust acceleration of the last frame (m per tick^2, 0 when none). */
  thrust = 0;

  /** `cx`, `cz`: the camera position (three's x and z) of the world's own point (the centre of the tile, where the chart is laid). */
  constructor(private B: Bodies, private cx: number, private cz: number) {}

  /** The eye's height above the followed body's sea level (m); 0 before there is a pose. */
  get altitude(): number { return this.pose ? this.pose.h : 0; }

  /** Forget the regime (the registry was rebuilt or the camera was put somewhere by hand): the next step starts from the tile. */
  reset(): void { this.mode = 'tile'; this.body = 0; this.pose = null; this.orbiter = null; this.thrust = 0; }

  /** The observer's place in the star's frame (metres) at `tick`: null out of the free regime, or without the server's bodies. */
  absPosition(tick: number): V3 | null {
    const B = this.B;
    if (this.mode !== 'free' || !this.pose || !B.hasEphemeris || !Number.isFinite(tick)) return null;
    if (this.orbiter) return [this.orbiter.pos[0], this.orbiter.pos[1], this.orbiter.pos[2]];
    return add(B.absAll(tick)[this.body], this.rel);
  }

  /** The observer's velocity relative to the followed body (m per tick, universe axes) when the thrusters are on, else null. */
  get velocity(): V3 | null {
    const o = this.orbiter;
    return o ? sub(o.vel, this.B.absVel(this.body, o.tick)) : null;
  }

  private tileOut(t: { east: number; north: number; h: number }): V3 { return [this.cx + t.east, t.h, this.cz - t.north]; }

  /**
   * Call once a frame after the controls have moved the camera. `drive`: the thrusters (see the top of the file); null for the kinematic free regime, where the camera's own movement this
   * frame is the displacement. On the tile it is ignored.
   */
  step(x: number, y: number, z: number, yaw: number, pitch: number, roll = 0, dt = 0, drive: Drive | null = null): FlightOut {
    const B = this.B;
    // Out in space the camera's attitude is its own: nothing levels it and no body's up is preferred. Back on the tile (a ground, a horizon) whatever roll it arrives with eases off.
    if (this.mode === 'tile') roll *= Math.exp(-Math.max(dt, 0) / ROLL_LEVEL_TAU_S);
    const out: FlightOut = { x, y, z, yaw, pitch, roll, changed: false };
    if (!B.ready) { this.pose = null; return out; }

    if (this.mode === 'tile') {
      const p = B.tilePose(x - this.cx, -(z - this.cz), y);
      if (!p) { this.pose = null; return out; }
      if (p.h > FREE_ABOVE_M) {
        const q = B.freePose(0, p.rel);
        if (q) {
          const v = rebaseAttitude(p.axes, q.axes, yaw, pitch, roll);
          out.yaw = v.yaw; out.pitch = v.pitch; out.roll = v.roll; out.changed = true;
          this.mode = 'free'; this.body = 0; this.rel = [p.rel[0], p.rel[1], p.rel[2]];
          this.pose = q; this.last = [x, y, z];
          return out;
        }
      }
      this.pose = p;
      return out;
    }

    // free regime
    const prev = this.pose;
    if (!prev) { this.mode = 'tile'; return out; }
    let body = this.body;
    let rel: V3;
    if (drive && B.hasEphemeris && Number.isFinite(drive.tick)) {
      // the observer is a body: advance it by the ticks the simulation has run, under the pulls and the thrust
      if (!this.orbiter) {
        const abs0 = B.absAll(drive.tick);
        this.orbiter = new Orbiter(add(abs0[body], this.rel), B.absVel(body, drive.tick), drive.tick);
      }
      const o = this.orbiter;
      const abs = B.absAll(o.tick);
      const g = strongest(B, abs, o.pos).a;
      const full = THRUST_G * g * Math.max(0, drive.gain);
      let th: V3 = [0, 0, 0];
      if (drive.brake) {
        const v = sub(o.vel, B.absVel(body, o.tick)), vl = len(v);
        const ticks = Math.max(drive.tick - o.tick, 1e-9);
        if (vl > 0) th = scale(v, -Math.min(full, vl / ticks) / vl);
        this.thrust = full;
      } else {
        const d = renderToUniverse(prev.axes, [drive.dir[0], drive.dir[1], drive.dir[2]]);
        const dl = len(d);
        if (dl > 1e-9) th = scale(d, full / dl);
        this.thrust = dl > 1e-9 ? full : 0;
      }
      const floor = B.list.map((b, i) => (i === 0 ? b.radius + 60e3 : b.radius + MIN_ROCK_ALT_M));
      o.advance(B, drive.tick, th, floor);
      const absN = B.absAll(o.tick);
      const posH = sub(o.pos, absN[0]);                       // relative to the home centre, as the registry has its bodies (fed at this tick, no offset out here)
      const nb = B.nearest(posH, body);
      if (nb !== body) body = nb;
      rel = sub(o.pos, absN[body]);
    } else {
      this.orbiter = null; this.thrust = 0;
      // the controls' displacement this frame, in last frame's render axes, as a displacement in the universe
      rel = add(this.rel, renderToUniverse(prev.axes, [x - this.last[0], y - this.last[1], z - this.last[2]]));
      const pos = add(B.list[body].pos, rel);
      const nb = B.nearest(pos, body);
      if (nb !== body) { body = nb; rel = sub(pos, B.list[body].pos); }
      // no landing on a body that has no tile
      if (body !== 0) {
        const b = B.list[body];
        if (Math.hypot(rel[0], rel[1], rel[2]) < b.radius + MIN_ROCK_ALT_M) rel = scale(norm(rel), b.radius + MIN_ROCK_ALT_M);
      }
    }
    const q = B.freePose(body, rel);
    if (!q) { this.reset(); return out; }

    if (body === 0 && q.h < TILE_BELOW_M) {
      // back to the tile: the camera is where the chart puts the point below the eye
      const t = B.tileOf(q.pos);
      const tp = t ? B.tilePose(t.east, t.north, t.h) : null;
      if (t && tp) {
        const v = rebaseAttitude(prev.axes, tp.axes, yaw, pitch, roll);
        const o = this.tileOut(t);
        this.mode = 'tile'; this.body = 0; this.pose = tp; this.orbiter = null; this.thrust = 0;
        return { x: o[0], y: o[1], z: o[2], yaw: v.yaw, pitch: v.pitch, roll: v.roll, changed: true };
      }
    }

    let o: V3;
    if (body === 0) {
      const t = B.tileOf(q.pos);
      o = t ? this.tileOut(t) : [this.cx, q.h, this.cz];
    } else o = [this.cx, q.h, this.cz];
    // The render axes turn every frame the observer moves (up is away from the body's centre), so the camera's attitude is re-expressed every frame, not only when the followed body changes:
    // the direction looked in and the way up stay fixed in the universe, the body does not pull the view toward itself as the observer passes over it, and a change of followed body (whose
    // up may be the opposite one) does not turn the camera over.
    {
      const v = rebaseAttitude(prev.axes, q.axes, yaw, pitch, out.roll);
      out.yaw = v.yaw; out.pitch = v.pitch; out.roll = v.roll;
    }
    out.x = o[0]; out.y = o[1]; out.z = o[2]; out.changed = true;
    this.body = body; this.rel = rel; this.pose = q; this.last = o;
    return out;
  }
}
